import React from 'react';

import Button from '~/components/core/Atomic/Buttons/Button';
import EmptyState from '~/components/core/EmptyState';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import SortableTable from '~/components/core/Tables/SortableTable';
import { serverDateTimeToLocal } from '~/DateTimeUtils';

import { PROMOTIONS_ROWS_PER_PAGE } from './constants';
import PromotionEnvBadge from './PromotionEnvBadge';
import PromotionStatusChip from './PromotionStatusChip';
import {
  assistantDraftedByLabel,
  deferredSectionsToRollBack,
  incomingSourceLabel,
  isRollbackable,
} from './promotionsUtils';
import type { IncomingPromotionRow } from './types';
import { PROMOTION_STATUS } from './types';

interface IncomingPromotionsTableProps {
  rows: IncomingPromotionRow[];
  isError: boolean;
  onView: (requestUuid: string) => void;
  onRollback: (row: IncomingPromotionRow) => void;
}

/** The target-side incoming promotions table. A pending row opens the review flow (approve/reject);
 * a resolved row opens read-only. Roll back is offered only where the client-verifiable BE eligibility rules
 * hold (see `isRollbackable`) — a DEPLOYED promotion row that is the latest deployment for the target, or a
 * rolled-back promotion whose latest rollback deferred blocked sections ("Roll back deferred sections") — so we
 * don't present an action guaranteed to be refused. The dialog's preview decides the rest (snapshots
 * present, drift/apply-refusal, sections still blocked) via `rollbackable: false`. */
const IncomingPromotionsTable: React.FC<IncomingPromotionsTableProps> = ({ rows, isError, onView, onRollback }) => {
  // The source env is blank for same-DB rows (the backend only populates it for cross-deployment pairings);
  // show the column only when at least one row is cross-deployment, so an all-same-DB list is unchanged.
  const hasSourceEnv = rows.some((row) => Boolean(row.source_deployment_id));
  const columns = [
    {
      id: 'request_uuid',
      label: 'Promotion ID',
      specialCell: (row: IncomingPromotionRow) => <span className="font-mono text-slate-900">{row.request_uuid}</span>,
    },
    {
      id: 'request_name',
      label: 'Name',
      specialCell: (row: IncomingPromotionRow) => <span>{row.request_name || '—'}</span>,
    },
    {
      id: 'source_org_external_id',
      label: 'Source',
      specialCell: (row: IncomingPromotionRow) => <span>{incomingSourceLabel(row)}</span>,
    },
    ...(hasSourceEnv
      ? [
          {
            id: 'source_deployment_id',
            label: 'Source env',
            specialCell: (row: IncomingPromotionRow) => <PromotionEnvBadge subDomain={row.source_deployment_id} />,
          },
        ]
      : []),
    {
      id: 'status',
      label: 'Status',
      specialCell: (row: IncomingPromotionRow) => <PromotionStatusChip status={row.status} />,
    },
    {
      id: 'requester_display_name',
      label: 'Requested by',
      specialCell: (row: IncomingPromotionRow) => (
        <span>{assistantDraftedByLabel(row) ?? row.requester_display_name}</span>
      ),
    },
    {
      id: 'approver_display_name',
      label: 'Approver',
      specialCell: (row: IncomingPromotionRow) => <span>{row.approver_display_name || '—'}</span>,
    },
    {
      id: 'created_at',
      label: 'Created',
      specialCell: (row: IncomingPromotionRow) => <span>{serverDateTimeToLocal(row.created_at)}</span>,
    },
    {
      id: 'actions',
      label: 'Actions',
      specialCell: (row: IncomingPromotionRow) => (
        <div className="flex items-center gap-x-8">
          <Button variant="text" color="primary" onClick={() => onView(row.request_uuid)}>
            {row.status === PROMOTION_STATUS.PENDING ? 'Review' : 'View'}
          </Button>
          {isRollbackable(row, rows) ? (
            <Button variant="text" color="primary" onClick={() => onRollback(row)}>
              {deferredSectionsToRollBack(row, rows).length > 0 ? 'Roll back deferred sections' : 'Roll back'}
            </Button>
          ) : null}
        </div>
      ),
      disableSort: true,
    },
  ];

  if (isError) {
    return <SkeletonTable rowsCount={3} columnsCount={columns.length} isError />;
  }

  return (
    <SortableTable
      columns={columns}
      rows={rows}
      defaultOrderColumn={columns.findIndex((c) => c.id === 'created_at')} // Created
      order="desc"
      stickyHeader
      emptyStateComponent={<EmptyState title="No incoming promotions" />}
      autoPaginateRowsPerPage={PROMOTIONS_ROWS_PER_PAGE}
    />
  );
};

export default IncomingPromotionsTable;
